import React, { FC, useCallback } from 'react';
import currency from 'currency.js';
import {
  MakePaymentResponseType,
  MakePaymentRequestType,
  GetPolicyPaymentMethodsResponseType,
  PaymentMethodType,
} from '@clearcover/core-app-types';
import { useHistory, useParams } from 'react-router-dom';
import classnames from 'classnames';
import * as Yup from 'yup';
import { Formik, FormikHelpers } from 'formik';
import { Button, Modal, ModalError, ModalLoading } from '@clearcover/atlas';
import { useLazyApi, useApi } from 'Shared/services/Api';
import { useToast } from 'Shared/components/Toast';
import {
  ConfirmationModal,
  FormModalHeader,
  FormModalContent,
  FormModalFooter,
} from 'Shared/components';
import { PolicyPaymentMethod } from '../PolicyPaymentMethod';
import styles from './styles.module.css';
import { ItemizedPaymentAmounts } from './ItemizedPayment';

export interface MakeRenewalPaymentFormProps {
  close: () => void;
}

const MAKE_RENEWAL_PAYMENT_SCHEMA = Yup.object({
  paymentMethodId: Yup.string().required('Required'),
});

type MakeRenewalPaymentFormValueType = {
  paymentMethodId: string;
};

export const MakeRenewalPaymentForm: FC<MakeRenewalPaymentFormProps> = ({
  close,
}) => {
  const { toast } = useToast();
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const history = useHistory();

  const [{ data, loading: optionsLoading, error: optionsError }] =
    useApi<MakePaymentResponseType>(`payments/${policyId}/options/renewal`);

  const [
    {
      data: policyPaymentMethods,
      loading: paymentMethodsLoading,
      error: paymentMethodsError,
    },
  ] = useApi<GetPolicyPaymentMethodsResponseType>(
    `/policies/${policyId}/paymentMethods`
  );

  const [, makeRenewalPayment] = useLazyApi<
    Omit<MakePaymentRequestType, 'paymentTotal'>
  >({
    method: 'POST',
    url: `/policies/${policyId}/applyRenewal`,
  });

  const handleFormSubmit = useCallback(
    (
      values: MakeRenewalPaymentFormValueType,
      {
        setStatus,
        setSubmitting,
      }: FormikHelpers<MakeRenewalPaymentFormValueType>
    ) => {
      return makeRenewalPayment({
        data: {
          paymentMethodId: values.paymentMethodId,
        },
      })
        .then(() => {
          toast({
            message: 'Payment made successfully.',
            type: 'success',
            position: 'bottom',
            withSecondaryNav: false,
          });
          history.push(`/policies/${policyId}`);
          close();
        })
        .catch((err) => {
          toast({
            message: err.response.data.message ?? err.message,
            type: 'error',
            position: 'top',
            withSecondaryNav: false,
            width: 'full',
            icon: 'info',
          });
          setSubmitting(false);
          setStatus(err.response.data.message ?? err.message);
        });
    },
    [makeRenewalPayment, close, toast, history, policyId]
  );

  if (optionsLoading || paymentMethodsLoading) {
    return <ModalLoading data-testid="otpayment-loading" />;
  }

  const initializationError = optionsError || paymentMethodsError;

  if (!!initializationError) {
    return (
      <ModalError
        data-testid="otpayment-error"
        errorMessage={initializationError.message}
        componentStack={initializationError.stack}
      />
    );
  }

  function findPaymentMethod({
    paymentMethods,
    paymentMethodId,
  }: {
    paymentMethods: Array<PaymentMethodType>;
    paymentMethodId: string;
  }): PaymentMethodType {
    const paymentMethod = paymentMethods.find(
      (pm) => pm.id === paymentMethodId
    );
    if (!paymentMethod) {
      throw Error('Payment method not found');
    }
    return paymentMethod;
  }

  const INITIAL_FORM_VALUES: MakeRenewalPaymentFormValueType = {
    paymentMethodId: policyPaymentMethods.defaultPaymentMethod.id,
  };

  return (
    <Formik
      initialValues={INITIAL_FORM_VALUES}
      onSubmit={handleFormSubmit}
      validationSchema={MAKE_RENEWAL_PAYMENT_SCHEMA}
      validateOnMount
    >
      {({ setFieldValue, isValid, values, isSubmitting, handleSubmit }) => {
        const paymentMethodType = findPaymentMethod({
          paymentMethods: policyPaymentMethods.paymentMethods,
          paymentMethodId: values.paymentMethodId,
        }).type;
        const totalAmount = currency(
          data.paymentFeeEnabledState
            ? paymentMethodType === 'bank'
              ? data.downPayment.remainingBalanceUsingBank
              : data.downPayment.remainingBalanceUsingCreditCard
            : data.downPayment.remainingBalance
        );
        return (
          <>
            <FormModalHeader>Make a Payment</FormModalHeader>
            <FormModalContent>
              <div className={styles.section}>
                <h1>Make a payment</h1>
                <PolicyPaymentMethod
                  policyPaymentMethods={policyPaymentMethods.paymentMethods}
                  value={values.paymentMethodId}
                  setValue={(value) => setFieldValue('paymentMethodId', value)}
                />
              </div>
              <div className={styles.section}>
                <div className={styles.paymentOptions}>
                  <div
                    className={styles.paymentOption}
                    data-testid="payment-amount-minimum"
                  >
                    <div
                      className={classnames(styles.radio, {
                        [styles.checked]: true,
                      })}
                    >
                      <div className={styles.radioOutter}>
                        <div className={styles.radioInner}></div>
                      </div>
                    </div>
                    <div className={styles.text}>Renewal payment</div>
                    <div className={styles.value}>
                      {currency(data.downPayment.premiumAmount, {
                        formatWithSymbol: true,
                      }).format()}
                    </div>
                  </div>
                </div>

                <ItemizedPaymentAmounts
                  chosenPaymentMethodType={paymentMethodType}
                  paymentFeeEnabledState={data.paymentFeeEnabledState}
                  paymentTypeFees={data.paymentTypeFees}
                  itemizedAmounts={data.downPayment}
                  totalAmount={totalAmount}
                />
              </div>
            </FormModalContent>
            <FormModalFooter
              showStatus={false}
              close={close}
              checkDirty={false}
              identifier={`${values.paymentMethodId}-make-payment`}
              confirmationModal={
                <Modal
                  variant="normal"
                  toggle={(openModal) => (
                    <Button
                      data-testid="renewal-payment-exit"
                      size={'md'}
                      variant="primary"
                      type="button"
                      onClick={openModal}
                      disabled={isSubmitting}
                    >
                      Continue
                    </Button>
                  )}
                >
                  {(close) => (
                    <ConfirmationModal
                      onSubmit={() => handleSubmit()}
                      close={close}
                      disabled={!isValid}
                      isSubmitting={isSubmitting}
                      variant="primary"
                      headerText="Confirm one time payment"
                      bodyText={`This will be a renewal payment of ${currency(
                        data.downPayment.remainingBalance,
                        {
                          formatWithSymbol: true,
                        }
                      ).format()}. Please confirm.`}
                    />
                  )}
                </Modal>
              }
            />
          </>
        );
      }}
    </Formik>
  );
};

MakeRenewalPaymentForm.displayName = 'MakeRenewalPaymentForm';
